import { geohashQueryBounds, distanceBetween, Geopoint } from 'geofire-common';

/**
 * Utility class for geolocation operations and coordinate validation
 * @class GeolocationHelper
 * @property {function} calculateDistanceInMeters - Calculate distance between two coordinates in meters using Haversine formula
 * @property {function} degreesToRadians - Convert degrees to radians
 * @property {function} isValidCoordinates - Validate latitude and longitude coordinates
 * @property {function} isValidLatLong - Validate latitude and longitude values
 * @property {function} isValidGeolocationPosition - Validate GeolocationPosition object
 */
export class GeolocationHelper {

    /**
     * Calculate distance between two coordinates using Haversine formula
     * @param lat1 Latitude of first point
     * @param lon1 Longitude of first point
     * @param lat2 Latitude of second point
     * @param lon2 Longitude of second point
     * @returns Distance in meters
     */
    static calculateDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371000; // Radius of the Earth in meters
        const dLat = this.degreesToRadians(lat2 - lat1);
        const dLon = this.degreesToRadians(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.degreesToRadians(lat1)) * Math.cos(this.degreesToRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in meters
    }

    /**
     * Converts degrees to radians
     * @param degrees Degrees
     * @returns Radians
     */
    static degreesToRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * Validates if the given coordinates are valid latitude and longitude
     * @param position Object containing latitude and longitude
     * @returns True if valid, false otherwise
     */
    static isValidCoordinates(position: { latitude: number, longitude: number }): boolean {
        if (!position) {
            return false;
        }
        return position.latitude >= -90 && position.latitude <= 90 && position.longitude >= -180 && position.longitude <= 180;
    }

    /**
     * Validates if the given latitude and longitude are valid
     * @param position Object containing latitude and longitude
     * @returns True if valid, false otherwise
     */
    static isValidLatLong(position: { latitude: number, longitude: number }): boolean {
        if (!position?.latitude || !position?.longitude) {
            return false;
        }
        return this.isValidCoordinates({ latitude: position.latitude, longitude: position.longitude });
    }

    /**
     * Validates if the given GeolocationPosition is valid
     * @param position GeolocationPosition object
     * @returns True if valid, false otherwise
     */
    static isValidGeolocationPosition(position: GeolocationPosition) {
        if (!position || !position.coords) {
            return false;
        }
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        return this.isValidCoordinates({ latitude: lat, longitude: lon });
    }
}

/**
 * Utility class for geohash operations using geofire-common library
 * @class GeoHashHelper
 * @property {function} getBoundsFromMiles - Get geohash bounds for a given location and radius in miles
 * @property {function} getBoundsFromKilometers - Get geohash bounds for a given location and radius in kilometers
 * @property {function} calculateDistance - Calculate distance between two points using geofire-common
 */
export class GeoHashHelper {

    /**
     * Get geohash bounds for a given location and radius in miles
     * @param lat Latitude
     * @param lng Longitude
     * @param radiusInMiles Radius in miles
     * @returns Array of geohash bounds
     */
    static getBoundsFromMiles(lat: number, lng: number, radiusInMiles: number): string[][] {
        const radiusInM = DistanceHelper.milesToMeters(radiusInMiles); // Convert miles to meters
        const center: Geopoint = [lat, lng];
        const bounds = geohashQueryBounds(center, radiusInM);
        return bounds;
    }

    /**
     * Get geohash bounds for a given location and radius in kilometers
     * @param lat Latitude
     * @param lng Longitude
     * @param radiusInKilometers Radius in kilometers
     * @returns Array of geohash bounds
     */
    static getBoundsFromKilometers(lat: number, lng: number, radiusInKilometers: number): string[][] {
        const radiusInM = radiusInKilometers * 1000; // Convert kilometers to meters
        const center: Geopoint = [lat, lng];
        const bounds = geohashQueryBounds(center, radiusInM);
        return bounds;
    }

    /**
     * Get geohash bounds for a given location and radius in kilometers
     * @param lat1 Latitude of the center point
     * @param lng1 Longitude of the center point
     * @param lat2 Latitude of the second point
     * @param lng2 Longitude of the second point
     * @returns Distance between the two points in meters
     */
    static calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const point1: Geopoint = [lat1, lng1];
        const point2: Geopoint = [lat2, lng2];
        return distanceBetween(point1, point2);
    }
}

/**
 * Utility class for distance calculations and unit conversions
 * @class DistanceHelper
 * @property {number} EARTH_RADIUS_MILES - Earth's radius in miles (3958.8)
 * @property {number} EARTH_RADIUS_KILOMETERS - Earth's radius in kilometers (6371.0)
 * @property {function} calculateDistanceInMeters - Calculate distance between two coordinates in meters
 * @property {function} calculateDistanceInMiles - Calculate distance between two coordinates in miles
 * @property {function} calculateDistanceInKilometers - Calculate distance between two coordinates in kilometers
 * @property {function} milesToKilometers - Convert miles to kilometers
 * @property {function} milesToMeters - Convert miles to meters
 * @property {function} kilometersToMiles - Convert kilometers to miles
 * @property {function} kilometersToMeters - Convert kilometers to meters
 * @property {function} metersToMiles - Convert meters to miles
 * @property {function} metersToKilometers - Convert meters to kilometers
 */
export class DistanceHelper {
    /**
     * Earth's radius in miles
     * @constant {number}
     * @default 3958.8
     */
    static readonly EARTH_RADIUS_MILES = 3958.8;
    /**
     * Earth's radius in kilometers
     * @constant {number}
     * @default 6371.0
     */
    static readonly EARTH_RADIUS_KILOMETERS = 6371.0;

    /**
     * Calculate distance between two coordinates in meters
     * @param lat1 Latitude of first point
     * @param lng1 Longitude of first point
     * @param lat2 Latitude of second point
     * @param lng2 Longitude of second point
     * @returns Distance in meters
     */
    static calculateDistanceInMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
        return GeolocationHelper.calculateDistanceInMeters(lat1, lng1, lat2, lng2);
    }

    /**
     * Calculate distance between two coordinates in miles
     * @param lat1 Latitude of first point
     * @param lng1 Longitude of first point
     * @param lat2 Latitude of second point
     * @param lng2 Longitude of second point
     * @returns Distance in miles
     */
    static calculateDistanceInMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const distanceInMeters = GeolocationHelper.calculateDistanceInMeters(lat1, lng1, lat2, lng2);
        return this.metersToMiles(distanceInMeters);
    }

    /**
     * Calculate distance between two coordinates in kilometers
     * @param lat1 Latitude of first point
     * @param lng1 Longitude of first point
     * @param lat2 Latitude of second point
     * @param lng2 Longitude of second point
     * @returns Distance in kilometers
     */
    static calculateDistanceInKilometers(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const distanceInMeters = GeolocationHelper.calculateDistanceInMeters(lat1, lng1, lat2, lng2);
        return this.metersToKilometers(distanceInMeters);
    }

    /**
     * Converts kilometers from miles
     * @param miles Miles
     * @returns Kilometers
     */
    static milesToKilometers(miles: number): number {
        return miles * 1.60934;
    }

    /**
     * Converts meters from miles
     * @param miles Miles
     * @returns Meters
     */
    static milesToMeters(miles: number): number {
        return miles * 1609.34;
    }

    /**
     * Converts miles from kilometers
     * @param kilometers Kilometers
     * @returns Miles
     */
    static kilometersToMiles(kilometers: number): number {
        return kilometers / 1.60934;
    }

    /**
     * Converts meters from kilometers
     * @param kilometers Kilometers
     * @returns Meters
     */
    static kilometersToMeters(kilometers: number): number {
        return kilometers * 1000;
    }

    /**
     * Converts miles from meters
     * @param meters Meters
     * @returns Miles
     */
    static metersToMiles(meters: number): number {
        return meters / 1609.34;
    }

    /**
     * Converts kilometers from meters
     * @param meters Meters
     * @returns Kilometers
     */
    static metersToKilometers(meters: number): number {
        return meters / 1000;
    }
}